/**
 * DesignRadarChart - デザインファイル準拠のレーダーチャートコンポーネント
 * assets/design/company_wide/your-report.html のjs-radar-chartを再現
 */
import React, { useEffect, useRef } from 'react';

export interface DesignRadarChartProps {
  /** A（論理）のスコア */
  scoreA: number;
  /** B（秩序）のスコア */
  scoreB: number;
  /** C（共感）のスコア */
  scoreC: number;
  /** D（創造）のスコア */
  scoreD: number;
  /** 比較用チャートかどうか */
  isCompare?: boolean;
  /** 最大値（デフォルト: 12） */
  maxValue?: number;
  /** クラス名 */
  className?: string;
}

/**
 * デザインファイル準拠のレーダーチャート
 * main.jsのRADAR_CHART関数のロジックをReactで実装
 */
export const DesignRadarChart: React.FC<DesignRadarChartProps> = ({
  scoreA,
  scoreB,
  scoreC,
  scoreD,
  isCompare = false,
  maxValue = 12,
  className = '',
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    if (!svgRef.current || !pathRef.current) return;

    const svg = svgRef.current;
    const path = pathRef.current;

    // SVGのサイズを取得
    const w = svg.clientWidth || 200;
    const h = svg.clientHeight || 200;
    const size = Math.min(w, h);

    // viewBoxを設定
    svg.setAttribute('viewBox', `0 0 ${size} ${size}`);

    const cx = size / 2;
    const cy = size / 2;
    const maxR = size / 2;

    // スコア値
    const values = [scoreA, scoreB, scoreC, scoreD];

    // 角度（デザインファイルと同じ）
    // A: 225°, B: 135°, C: 45°, D: 315°
    const angles = [
      225 * Math.PI / 180, // A - 左上
      135 * Math.PI / 180, // B - 左下
      45 * Math.PI / 180,  // C - 右下
      315 * Math.PI / 180, // D - 右上
    ];

    // パスを生成
    let d = '';
    values.forEach((v, i) => {
      const ratio = v / maxValue;
      const r = maxR * ratio;
      const x = cx + Math.cos(angles[i]) * r;
      const y = cy + Math.sin(angles[i]) * r;
      d += (i === 0 ? 'M' : 'L') + x + ' ' + y + ' ';
    });
    d += 'Z';

    path.setAttribute('d', d);
  }, [scoreA, scoreB, scoreC, scoreD, maxValue]);

  const lineClassName = isCompare 
    ? 'js-radar-chart_line _compare-color' 
    : 'js-radar-chart_line';

  return (
    <svg
      ref={svgRef}
      className={`js-radar-chart ${isCompare ? 'js-radar-chart-compare' : ''} ${className}`}
      data-a={scoreA}
      data-b={scoreB}
      data-c={scoreC}
      data-d={scoreD}
      style={{ width: '100%', height: '100%' }}
    >
      <path ref={pathRef} className={lineClassName} d="" />
    </svg>
  );
};

export default DesignRadarChart;
